import { filterClasses } from "@swivl-mono/helpers";
import { Channel, IsSessionTakenOver, SessionInterface, UserInterface } from "@swivl-mono/types";
import { useEffect, useRef, useState } from "react";
import { ChannelColors } from "../../../../Helpers/Conversations/Conversations.Helper";
import useAutoFocus from "../../../../Hooks/useAutoFocus";
import useKeyPressAction from "../../../../Hooks/useKeyPressAction";
import { AIModel } from "../../../../Models/AI/AI.Model";
import { Member } from "../../../../Models/Member/Member.Current";
import { QuestsModel } from "../../../../Models/Quests/Quests.Model";
import { QuickReplyModel } from "../../../../Models/QuickReply/QuickReply.Model";
import { UserConversationsInterface } from "../../../../Models/Users/Users.Model";
import { QuickRepliesDropdown } from "../QuickRepliesDropdown";
import { ChatFooterNotTakenOverComponent } from "./Components/ChatFooter.NotTakenOverComponent";
import { ChatFooterVoice } from "./Components/ChatFooter.Voice";
import { ChatForm } from "./Components/ChatForm";

interface BaseMultiChannelColumnFooterProps {
  sendMessage: (message: string) => void;
  sendTypingFrame: (typing: boolean) => void;
  channel: Channel,
  user: UserInterface,
  session?: SessionInterface
  conversation?: UserConversationsInterface,
  selectedUserId: number | undefined;
  isQuickRepliesDropdownOpen: boolean;
  toggleAutoCompleteEnabled: () => void;
  isAutoCompleteEnabled: boolean;
  handleToggleQuickRepliesDropdown: (isOpen?: boolean) => void;
}

export function MultiChannelColumnFooter(props: BaseMultiChannelColumnFooterProps) {
  const {
    user,
    channel,
    conversation,
    session,
    selectedUserId,
    sendMessage,
    sendTypingFrame,
    isQuickRepliesDropdownOpen,
    isAutoCompleteEnabled,
    toggleAutoCompleteEnabled,
    handleToggleQuickRepliesDropdown
  } = props;
  const { quickReplies, isLoadingQuickReplies } = QuickReplyModel.useState();
  const { agentAutoCompleteText, isLoadingAutoCompleteText } = AIModel.useState((s) => s);
  console.log('Transcript update : agentAutoCompleteText :', agentAutoCompleteText, isLoadingAutoCompleteText);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  useAutoFocus(textAreaRef, [channel, selectedUserId], { onlyOnMount: true });
  const [text, setText] = useState("");
  const [centerX, setCenterX] = useState(0);
  const isSessionTakenOverByMe = IsSessionTakenOver(conversation?.sessions?.[channel], Member.id);
  const isAgentAssistEnabled = isSessionTakenOverByMe && isAutoCompleteEnabled;
  const hasAutoCompleteText = isAgentAssistEnabled && isAutoCompleteEnabled && agentAutoCompleteText?.length ? true : false;
  const isAutoCompleteLoading = isAgentAssistEnabled && isAutoCompleteEnabled && isLoadingAutoCompleteText;

  useKeyPressAction({
    key: "Enter",
    callback: (e) => {
      e.preventDefault();
      if (textAreaRef.current) {

        if (text.trim()) {
          sendMessage(text);
          setText("");
        }
      }
    },
    targetEl: textAreaRef.current
  })

  useKeyPressAction({
    key: "Tab",
    callback: (e) => {
      e.preventDefault();
      if (hasAutoCompleteText) {
        e.stopPropagation();
        e.preventDefault();
        setText(agentAutoCompleteText);
        setTimeout(() => {
          if (textAreaRef?.current) {
            (textAreaRef.current as any).focus();
          }
        }, 100);
      }
    },
    targetEl: textAreaRef.current
  })

  const { workflows } = QuestsModel.useState(s => s);

  useEffect(() => {
    QuestsModel.loadQuestsList(false, true)
  }, [])

  useEffect(() => {
    QuickReplyModel.loadQuickReplies(workflows)
  }, [workflows])

  const currentChannelColor = ChannelColors[channel] || ChannelColors[Channel.WEB];

  /**
   *  scenarios for footer:
   *  1. assigned agent is the same as the user (no takeover)
   *  2. assigned agent is different from the user (takeover button)
   *  3. assigned agent is not set and chatbot is active (takeover)
   *  4. agent is on call and changes channel to other account
   *  */

  useEffect(() => {
    const calculateCenterX = () => {
      const columnWidth = window.innerWidth / 3;
      const barWidth = columnWidth - (isSessionTakenOverByMe || channel === Channel.VOICE ? 32 : 0); // 32px for padding
      const centerX = (window.innerWidth - barWidth) / 2;
      setCenterX(centerX);
    }

    calculateCenterX();
    window.addEventListener("resize", calculateCenterX);
    return () => window.removeEventListener("resize", calculateCenterX);
  }, [window.innerWidth, isSessionTakenOverByMe, channel])


  const handleSetText = (newText: string) => {
    sendTypingFrame(true);
    setText(newText);
    sendTypingFrame(false);
  }


  if (channel === Channel.VOICE) {
    return <ChatFooterVoice centerX={centerX} user={user} session={session}
    />
    // return (
    //   <AnimatePresence mode="wait">
    //     <motion.div
    //       className="p-4 flex fixed h-[64px] bottom-4 w-[calc(33.33%-32px)] items-center justify-between rounded-md shadow-lg bg-white"
    //       key={position}
    //       initial={{ opacity: 0, translateY: 80 }}
    //       animate={{ opacity: 1, translateY: isCallBarOpen ? 80 : 0 }}
    //       exit={{ opacity: 0, translateY: 80 }}
    //       transition={{ duration: 0.3, ease: "easeInOut" }}
    //       style={{ right: centerX }}
    //     >
    //       <span className="text-sm font-semibold">Voice Call</span>
    //       <Button
    //         gradient={{ main: supportButtonStyles.yellow.main, accent: supportButtonStyles.yellow.accent }}
    //         onClick={handleCall}
    //         variant={ButtonVariant.Tertiary}
    //       >
    //         <FaPhone /> Call {user?.firstName || "User"}
    //       </Button>
    //     </motion.div>
    //   </AnimatePresence>
    // );
  }

  return (
    <div
      className={
        filterClasses(
          "fixed bottom-4 p-3 min-h-[144px] h-auto overflow-hidden grid bg-white transition-transform duration-300 ease-in-out",
          isSessionTakenOverByMe ? "rounded-md w-[calc(33.33%-32px)] shadow-lg" : "shadow-none border-t w-1/3 border-t-grey-200 border-l rounded-none",
          isQuickRepliesDropdownOpen ? "grid-rows-[1fr_auto]" : "grid-rows-none"
        )
      }
      style={{ right: centerX }}
    >
      {isSessionTakenOverByMe && session ? (
        <>
          <ChatForm
            session={session}
            currentChannelColor={currentChannelColor}
            sendMessage={sendMessage}
            isQuickRepliesDropdownOpen={isQuickRepliesDropdownOpen}
            handleToggleQuickRepliesDropdown={handleToggleQuickRepliesDropdown}
            isAutoCompleteEnabled={isAutoCompleteEnabled}
            toggleAutoCompleteEnabled={toggleAutoCompleteEnabled}
            handleSetText={handleSetText}
            text={text}
            isAutoCompleteLoading={isAutoCompleteLoading}
            hasAutoCompleteText={hasAutoCompleteText}
            agentAutoCompleteText={agentAutoCompleteText}
            textAreaRef={textAreaRef}
          />

          <QuickRepliesDropdown
            isQuickRepliesDropdownOpen={isQuickRepliesDropdownOpen}
            handleToggleQuickRepliesDropdown={handleToggleQuickRepliesDropdown}
            quickReplies={quickReplies}
            isLoading={isLoadingQuickReplies}
            handleSetText={handleSetText}
            user={user}
          />
        </>
      ) :
        <ChatFooterNotTakenOverComponent
          user={user}
          conversation={conversation}
          session={session}
          channel={channel}
          currentChannelColor={currentChannelColor}
        />
      }
    </div>
  );
}

